import React, { useState, useEffect } from 'react';
import { getAllPosts } from '../../services/postService';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Avatar, 
  Container, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Box, 
  Chip,
  ButtonGroup
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ChatIcon from '@mui/icons-material/Chat';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ViewListIcon from '@mui/icons-material/ViewList';
import './TownSquare.css';
import 'react-quill-new/dist/quill.snow.css';

/**
 * Conference Clamp inside joke - cycling through mispronunciations
 */
const conferenceClampNames = [
  'Conference Clamp',
  'Conference Clasp', 
  'Conference Clam',
  'Comfy Clam'
];

// Component for expandable post content with normalized height
const ExpandableContent = ({ content, maxLength = 200, onExpand }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!content) {
    // Return empty box with normalized height to maintain card consistency
    return <Box sx={{ height: '72px', mb: 2 }} />;
  }
  
  const plainText = content.replace(/<[^>]*>/g, '');
  const shouldTruncate = plainText.length > maxLength;
  
  const handleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(true);
    if (onExpand) onExpand(); // Notify parent component
  };
  
  return (
    <Box sx={{ mb: 2, minHeight: isExpanded ? 'auto' : '72px', display: 'flex', flexDirection: 'column' }}>
      <Box 
        sx={{ 
          color: '#6b4f2c',
          fontFamily: '"Georgia", serif',
          fontSize: '0.9rem',
          lineHeight: 1.6,
          flexGrow: 1,
          display: isExpanded ? 'block' : '-webkit-box',
          WebkitLineClamp: isExpanded ? 'none' : 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          '& p': { margin: 0, color: 'inherit' },
          '& strong': { color: '#5c3c10', fontWeight: 'bold' },
          '& em': { fontStyle: 'italic' }
        }}
        dangerouslySetInnerHTML={{ __html: content }} 
      />
      {shouldTruncate && !isExpanded && (
        <Button
          size="small"
          onClick={handleExpand}
          sx={{
            color: '#8b4513',
            fontFamily: '"Georgia", serif',
            fontSize: '0.8rem',
            textTransform: 'none',
            p: 0,
            mt: 0.5,
            alignSelf: 'flex-start',
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#704214',
              textDecoration: 'underline'
            }
          }}
        >
          Read more...
        </Button>
      )}
      {isExpanded && shouldTruncate && (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(false);
            if (onExpand) onExpand(); // Notify parent to reset height
          }}
          sx={{
            color: '#8b4513',
            fontFamily: '"Georgia", serif',
            fontSize: '0.8rem',
            textTransform: 'none',
            p: 0,
            mt: 0.5,
            alignSelf: 'flex-start',
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#704214',
              textDecoration: 'underline'
            }
          }}
        >
          Show less
        </Button>
      )}
    </Box>
  );
};

// Post type configuration for modern styling
const getPostTypeConfig = (postType) => {
  const configs = {
    inCharacter: {
      backgroundColor: '#f5e8c3',
      border: '2px solid #d2b48c',
      hoverBorderColor: '#b8860b',
      titleColor: '#5c3c10',
      chipColor: '#8b4513',
      icon: <TheaterComedyIcon />,
      label: 'In Character'
    },
    outOfCharacter: {
      backgroundColor: '#f0f0f0',
      border: '2px solid #a9a9a9',
      hoverBorderColor: '#808080',
      titleColor: '#333',
      chipColor: '#666',
      icon: <ChatIcon />,
      label: 'Out of Character'
    },
    dungeonMaster: {
      backgroundColor: '#e6d8ad',
      border: '3px solid #b8860b',
      hoverBorderColor: '#DAA520',
      titleColor: '#800000',
      chipColor: '#b8860b',
      icon: <AccountBalanceIcon />,
      label: 'Dungeon Master'
    }
  };
  
  return configs[postType] || configs.outOfCharacter;
};

// Individual Post Card Component with expansion state
const PostCard = ({ post, onClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const postTypeConfig = getPostTypeConfig(post.postType);
  
  return (
    <Card
      onClick={() => onClick(post)}
      sx={{
        cursor: 'pointer',
        backgroundColor: postTypeConfig.backgroundColor,
        border: postTypeConfig.border,
        borderRadius: 2,
        minHeight: '280px', // Minimum normalized height, can grow when expanded
        height: isExpanded ? 'auto' : '280px', // Dynamic height based on expansion
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
          borderColor: postTypeConfig.hoverBorderColor || postTypeConfig.borderColor
        },
        position: 'relative',
        overflow: 'visible'
      }}
    >
      {/* Post Type Indicator */}
      <Box sx={{ position: 'absolute', top: -8, left: 16 }}>
        <Chip
          icon={postTypeConfig.icon}
          label={postTypeConfig.label}
          size="small"
          sx={{
            backgroundColor: postTypeConfig.chipColor,
            color: 'white',
            fontFamily: '"Georgia", serif',
            fontSize: '0.7rem',
            '& .MuiChip-icon': { 
              color: 'white !important',
              fontSize: '0.9rem'
            }
          }}
        />
      </Box>

      <CardContent sx={{ pt: 3, pb: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, height: '100%' }}>
          {/* Main Content */}
          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography 
              variant="h6" 
              component="h3"
              sx={{ 
                fontFamily: '"Georgia", serif',
                color: postTypeConfig.titleColor,
                fontWeight: 'bold',
                mb: 1,
                fontSize: { xs: '1.1rem', md: '1.25rem' }
              }}
            >
              {post.title}
            </Typography>
            
            <ExpandableContent 
              content={post.content} 
              onExpand={() => setIsExpanded(!isExpanded)} 
            />
            
            {/* Post Meta - pushed to bottom */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mt: 'auto', // This pushes the meta to the bottom
              pt: 1
            }}>
              <Typography 
                variant="caption"
                sx={{ 
                  fontFamily: '"Georgia", serif',
                  color: '#704214',
                  fontSize: '0.75rem'
                }}
              >
                by <strong>
                  {post.postType === 'inCharacter' && post.characterName ? 
                    post.characterName : 
                    (post.userName || 'Anonymous')
                  }
                </strong>
              </Typography>
              <Typography 
                variant="caption"
                sx={{ 
                  fontFamily: '"Georgia", serif',
                  color: '#5c3c10',
                  fontStyle: 'italic',
                  fontSize: '0.75rem'
                }}
              >
                • {post.createdAt?.toDate ? 
                  new Date(post.createdAt.toDate()).toLocaleDateString(undefined, { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  }) : 
                  'Unknown date'
                }
              </Typography>
            </Box>
          </Box>

          {/* Character Avatar for IC posts */}
          {post.postType === 'inCharacter' && (
            <Box sx={{ flexShrink: 0 }}>
              {post.characterImageUrl ? (
                <Avatar 
                  src={post.characterImageUrl} 
                  alt={post.characterName || 'Character'} 
                  sx={{ 
                    width: 50, 
                    height: 50, 
                    border: '2px solid #d2b48c',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
              ) : (
                <Avatar sx={{ 
                  width: 50, 
                  height: 50, 
                  backgroundColor: '#8b4513', 
                  border: '2px solid #d2b48c',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <PersonIcon sx={{ fontSize: '1.5rem', color: '#f5f5dc' }} />
                </Avatar>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const TownSquare = () => {  const [allPosts, setAllPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentFilter, setCurrentFilter] = useState('all');
  const navigate = useNavigate();
  
  const [currentClampName, setCurrentClampName] = useState(() => 
    conferenceClampNames[Math.floor(Math.random() * conferenceClampNames.length)]
  );

  /**
   * Occasionally changes the Conference Clamp name for humor
   */
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() < 0.3) {
        setCurrentClampName(conferenceClampNames[Math.floor(Math.random() * conferenceClampNames.length)]);
      }
    }, 30000 + Math.random() * 30000);

    return () => clearInterval(interval);
  }, []);

  /**
   * Fetches all posts from the service and sets initial state
   */
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const fetchedPosts = await getAllPosts();
        setAllPosts(fetchedPosts);
        setFilteredPosts(fetchedPosts);
        setError(null);
      } catch (err) {
        console.error("Error fetching posts:", err);
        setError('Failed to load posts. Please try refreshing the page.');
        setAllPosts([]);
        setFilteredPosts([]);
      } finally {
        setLoading(false);
      }
    };    fetchPosts();
  }, []);

  /**
   * Filters posts based on current filter selection
   */
  useEffect(() => {
    if (currentFilter === 'all') {
      setFilteredPosts(allPosts);
    } else {
      setFilteredPosts(allPosts.filter(post => post.postType === currentFilter));
    }
  }, [currentFilter, allPosts]);

  /**
   * Navigates to detailed post view
   */
  const handlePostClick = (post) => {
    navigate(`/post/${post.id}`, { state: { post } });
  };

  if (loading) {
    return <div className="town-square-loading">Connecting to the {currentClampName}...</div>;
  }

  if (error) {
    return <div className="town-square-error">The {currentClampName} seems to be malfunctioning: {error}</div>;
  }

  return (
    <Container 
      maxWidth={false}
      sx={{ 
        backgroundColor: '#f5f5dc',
        minHeight: '100vh',
        py: 4,
        px: { xs: 2, sm: 3, md: 4 }
      }}
    >
      <Box sx={{ 
        maxWidth: { xs: '100%', sm: '900px', md: '1100px', lg: '1300px', xl: '1500px' }, 
        margin: '0 auto'
      }}>
        {/* Modern Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            component="h1"
            sx={{ 
              fontFamily: '"Georgia", serif',
              color: '#8b4513',
              fontWeight: 'bold',
              mb: 1,
              textShadow: '1px 1px 1px #deb887'
            }}
          >
            The {currentClampName}
          </Typography>
          <Typography 
            variant="subtitle1"
            sx={{ 
              fontStyle: 'italic', 
              color: '#8b4513',
              fontFamily: '"Georgia", serif'
            }}
          >
            {currentClampName === 'Conference Clamp' ? 
              'Your reliable communication device' : 
              '(We really need to get this thing labeled properly...)'
            }
          </Typography>
        </Box>

        {/* Modern Action Bar */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 4,
          p: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <ButtonGroup 
            variant="outlined" 
            sx={{ 
              '& .MuiButton-root': {
                fontFamily: '"Georgia", serif',
                fontSize: '0.85rem',
                px: { xs: 1.5, sm: 2 },
                py: 1,
                border: '1px solid #8b4513',
                color: '#5c3c10',
                '&:hover': {
                  backgroundColor: '#8b4513',
                  color: '#f5f5dc'
                }
              },
              '& .MuiButton-root.active': {
                backgroundColor: '#8b4513',
                color: '#f5f5dc',
                '&:hover': {
                  backgroundColor: '#704214'
                }
              }
            }}
          >
            <Button 
              onClick={() => setCurrentFilter('all')} 
              className={currentFilter === 'all' ? 'active' : ''}
              startIcon={<ViewListIcon />}
            >
              All Posts
            </Button>
            <Button 
              onClick={() => setCurrentFilter('outOfCharacter')} 
              className={currentFilter === 'outOfCharacter' ? 'active' : ''}
              startIcon={<ChatIcon />}
            >
              OOC
            </Button>
            <Button 
              onClick={() => setCurrentFilter('inCharacter')} 
              className={currentFilter === 'inCharacter' ? 'active' : ''}
              startIcon={<TheaterComedyIcon />}
            >
              In Character
            </Button>
            <Button 
              onClick={() => setCurrentFilter('dungeonMaster')} 
              className={currentFilter === 'dungeonMaster' ? 'active' : ''}
              startIcon={<AccountBalanceIcon />}
            >
              DM Posts
            </Button>
          </ButtonGroup>
          
          <Button
            component={Link}
            to="/create-post"
            variant="contained"
            sx={{
              fontFamily: '"Georgia", serif',
              backgroundColor: '#d98d2a',
              color: '#F5F5F5',
              px: 3,
              py: 1.5,
              fontSize: '0.9rem',
              textTransform: 'none',
              boxShadow: '2px 2px 8px rgba(0,0,0,0.2)',
              '&:hover': {
                backgroundColor: '#c77a1a',
                transform: 'translateY(-2px)',
                boxShadow: '2px 4px 12px rgba(0,0,0,0.25)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            📢 Broadcast a Message
          </Button>
        </Box>

        {/* No Posts Message */}
        {filteredPosts.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#5c3c10', 
                fontStyle: 'italic', 
                fontFamily: '"Georgia", serif' 
              }}
            >
              {currentFilter === 'all' 
                ? `The ${currentClampName} is silent. Be the first to post something!` 
                : `No posts found for filter: ${currentFilter}.`}
            </Typography>
          </Box>
        )}

        {/* Modern Posts Grid - 2 per row */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)' 
          },
          gap: 3,
          alignItems: 'start'
        }}>
          {filteredPosts.map(post => (
            <PostCard 
              key={post.id}
              post={post}
              onClick={handlePostClick}
            />
          ))}
        </Box>
      </Box>
    </Container>
  );
};

export default TownSquare;
